import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getCookieConsent } from '../components/CookieConsentBanner';

// GA4 Measurement ID — hardcoded to match the static tag in index.html
const GA_MEASUREMENT_ID = 'G-NS2XTCM3V6';

/**
 * initGAConsentDefaults is now a no-op.
 * Consent defaults are set inline in index.html BEFORE gtag.js loads,
 * which is the correct order required by GA4 Consent Mode v2.
 */
export function initGAConsentDefaults() {
  // Handled statically in index.html — nothing to do here
}

export function useGoogleAnalytics() {
  const location = useLocation();

  // Fire page_view on every route change and update consent state
  useEffect(() => {
    if (typeof window.gtag !== 'function') return;

    const consent = getCookieConsent();
    const analyticsGranted = consent?.analytics === true;

    // Update consent state so GA4 knows the current user preference
    window.gtag('consent', 'update', {
      analytics_storage: analyticsGranted ? 'granted' : 'denied',
      functionality_storage: consent?.preferences ? 'granted' : 'denied',
    });

    // Always fire page_view — GA4 consent mode handles anonymisation
    window.gtag('event', 'page_view', {
      page_path: location?.pathname + (location?.search || ''),
      page_title: document.title,
    });
  }, [location]);
}

/**
 * Track a custom GA4 event
 */
export function trackEvent(eventName, params = {}) {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
}

export function trackSignUp(method = 'email') {
  trackEvent('sign_up', { method });
}

export function trackSignIn(method = 'email') {
  trackEvent('login', { method });
}

export function trackButtonClick(buttonId, page) {
  trackEvent('button_click', {
    button_id: buttonId,
    page: page || window.location?.pathname,
  });
}

export function trackFormSubmit(formId, page) {
  trackEvent('form_submit', {
    form_id: formId,
    page: page || window.location?.pathname,
  });
}

export function trackPremiumUpgrade(planType = 'premium', billingInterval = 'monthly') {
  trackEvent('purchase', {
    event_category: 'conversion',
    event_label: planType,
    billing_interval: billingInterval,
    value: 1,
    currency: 'GBP',
  });
  trackEvent('premium_upgrade', {
    plan_type: planType,
    billing_interval: billingInterval,
  });
}

export function trackDailyCheckIn() {
  trackEvent('daily_check_in_created', {
    event_category: 'engagement',
    event_label: 'check_in_submitted',
    value: 1,
  });
}

export function trackDoctorExport(format = 'pdf') {
  trackEvent('doctor_export_completed', {
    event_category: 'conversion',
    event_label: format,
    export_format: format,
    value: 1,
  });
}

export function trackLandingPageVisit() {
  trackEvent('landing_page_visit', {
    event_category: 'funnel',
    timestamp: new Date()?.toISOString(),
    traffic_source: document.referrer || 'direct',
    page_path: window.location?.pathname,
  });
}

export function trackSignupClick(ctaLabel = 'cta') {
  trackEvent('signup_click', {
    event_category: 'funnel',
    cta_label: ctaLabel,
    page_path: window.location?.pathname,
  });
}

export function trackFirstCheckinComplete() {
  trackEvent('first_checkin_complete', {
    event_category: 'funnel',
    event_label: 'first_entry_submitted',
    value: 1,
  });
}

export function trackDoctorReportGenerated(reportType = 'consultation_summary') {
  trackEvent('doctor_report_generated', {
    event_category: 'funnel',
    report_type: reportType,
    value: 1,
  });
}

export function trackSignInClick(location = 'navbar') {
  trackEvent('sign_in_click', {
    event_category: 'funnel',
    location,
    page_path: window.location?.pathname,
  });
}

export function trackRegistrationComplete(method = 'email') {
  trackEvent('registration_complete', {
    event_category: 'conversion',
    method,
    value: 1,
  });
}

export function trackFeatureGateModalOpen(featureName = 'unknown') {
  trackEvent('feature_gate_modal_open', {
    event_category: 'engagement',
    feature_name: featureName,
  });
}

export function trackFeatureGateUpgradeClick(featureName = 'unknown') {
  trackEvent('feature_gate_upgrade_click', {
    event_category: 'conversion',
    feature_name: featureName,
  });
}

export function trackFeatureGateReferClick(featureName = 'unknown') {
  trackEvent('feature_gate_refer_click', {
    event_category: 'engagement',
    feature_name: featureName,
  });
}

export function trackReferralProgramView(page = 'referral_program') {
  trackEvent('referral_program_view', {
    event_category: 'engagement',
    page,
  });
}

export function trackReferralShareClick(channel = 'unknown') {
  trackEvent('referral_share_click', {
    event_category: 'engagement',
    channel,
  });
}

export function trackReferralCompletion(milestoneCount = 1, rewardType = 'unknown') {
  trackEvent('referral_completion', {
    event_category: 'conversion',
    milestone_count: milestoneCount,
    reward_type: rewardType,
    value: 1,
  });
}

export function trackDoctorReportSharing(channel = 'unknown', reportPeriod = '') {
  trackEvent('doctor_report_shared', {
    event_category: 'engagement',
    channel,
    report_period: reportPeriod,
    value: 1,
  });
}

function trackFeedbackSubmit(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: trackFeedbackSubmit is not implemented yet.', args);
  return null;
}

export { trackFeedbackSubmit };
function trackCaregiverInvite(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: trackCaregiverInvite is not implemented yet.', args);
  return null;
}

export { trackCaregiverInvite };